import { useEffect, useMemo, useRef } from 'react';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { useEffectOnce } from 'react-use';
import { playSound, Sound } from '~/assets/sounds';
import { rage, useObservable } from '~/rage';
import { Layouts, PhoneApps } from '~/shared';
import { BackButton } from '../../components/BackButton';
import { Notification } from '../../components/Notification';
import { PhoneCase } from '../../components/PhoneCase';
import { TopRow } from '../../components/TopRow';
import { appComponents } from '../../models/PhoneAppList';
import {
	resetStore,
	setActiveApp,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import {
	ScreenContainer,
	StyledCloseZoneLeft,
	StyledCloseZoneRight,
	StyledCloseZoneTop,
	StyledContainer,
	StyledScreen,
	StyledScreenLight,
	StyledScreenMask,
} from './Phone.styled';

const Phone = () => {
	const dispatch = useLayoutDispatch();
	const activeApp = useLayoutSelector((state) => state.phone.activeApp);
	const sdkActiveApp = useObservable(rage.phone.getActiveApp());
	const settings = useObservable(rage.phone.getSettings());
	const AppComponent = useMemo(() => appComponents[activeApp], [activeApp]);
	const hasAppComponent = AppComponent !== null;
	const notificationState = useObservable(rage.phone.getNotificationState());
	const isVisible = useObservable(
		rage.gui.getLayoutVisibleState(Layouts.Phone),
	);
	const closeSoundPlayedRef = useRef(false);
	const prevActiveAppRef = useRef<PhoneApps | null>(null);

	const handleClose = () => {
		rage.phone.exit().catch((err) => rage.notifications.showException(err));
	};

	useEffect(() => {
		if (sdkActiveApp) {
			dispatch(setActiveApp(sdkActiveApp));
		}
	}, [sdkActiveApp]);

	useEffect(() => {
		// Для камеры выход не нужен
		const prevApp = prevActiveAppRef.current;

		if (prevApp && prevApp !== activeApp) {
			if (prevApp === PhoneApps.GPS) {
				rage.phone
					.closeGps()
					.catch((err) => rage.notifications.showException(err));
			} else if (prevApp === PhoneApps.ADS) {
				rage.phone
					.closeAds()
					.catch((err) => rage.notifications.showException(err));
			}
		}

		prevActiveAppRef.current = activeApp;
	}, [activeApp]);

	useEffect(() => {
		if (isVisible === false && !closeSoundPlayedRef.current) {
			playSound(Sound.LockPad);
			closeSoundPlayedRef.current = true;
		}
	}, [isVisible]);

	useEffectOnce(() => {
		playSound(Sound.UnlockPad);

		return () => {
			dispatch(resetStore());
		};
	});

	return (
		<>
			<StyledCloseZoneTop onClick={handleClose} />
			<StyledCloseZoneLeft onClick={handleClose} />
			<StyledCloseZoneRight onClick={handleClose} />
			<StyledContainer
				className={Layouts.Phone}
				$showNotification={!!notificationState}
			>
				<PhoneCase />
				<StyledScreen />
				<StyledScreenMask className="screen-mask" />
				<ScreenContainer data-phone-screen="" $brightness={settings.brightness}>
					<TopRow />
					<SwitchTransition>
						<CSSTransition
							timeout={300}
							classNames={'apps'}
							key={activeApp}
							addEndListener={(node, done) =>
								node.addEventListener('transitionend', done, false)
							}
						>
							{notificationState ? (
								<Notification />
							) : hasAppComponent ? (
								<AppComponent />
							) : (
								<span />
							)}
						</CSSTransition>
					</SwitchTransition>
					<BackButton />
					<StyledScreenLight />
				</ScreenContainer>
			</StyledContainer>
		</>
	);
};

export { Phone };
